import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router";
import { request } from "../../config/request.ts";
import DataTable, { Column } from "../../shared/DataTable/index.tsx";
import PageHeader from "../../shared/PageHeader/index.tsx";
import DeleteConfirmation from "../../shared/DeleteConfirmation/index.tsx";
import StatusChip from "../../shared/StatusChip/index.tsx";
import ActionIcons from "../../shared/ActionIcons/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import { imagepathReturn } from "../../config/formatters.ts";

interface Employee {
  id: number;
  name: string;
  phone: string;
  familyContactNo: string;
  gender: string;
  bloodGroup: string;
  address: string;
  joiningDate: string;
  status: string;
  salary: number;
  employmentType: string;
  photo: string | null;
  idProof: string | null;
}

export default function EmployeesList() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getEmployees();
  }, [page, limit, search]);

  const getEmployees = async () => {
    setLoading(true);
    const response = await request.get("api/getEmployees");
    setEmployees(response.data.data);
    setTotalCount(response.data.count);
    setLoading(false);
  };

  const handlePaginationChange = (newPage: number, newLimit: number) => {
    setPage(newPage);
    setLimit(newLimit);
  };

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: 0,
    title: null,
  });

  const columns: Column[] = [
    {
      id: "slNo",
      label: "Sl.No",
      minWidth: 60,
      format: (_value, _row, index) => (
        <Box component="span" sx={{ fontWeight: 600, color: "#64748b" }}>
          {page * limit + index + 1}
        </Box>
      ),
    },
    {
      id: "name",
      label: "Name",
      minWidth: 150,
      format: (value) => value,
    },
    {
      id: "phone",
      label: "Phone",
      minWidth: 130,
      format: (value) => value,
    },
    {
      id: "gender",
      label: "Gender",
      minWidth: 80,
      format: (value) => value,
    },
    {
      id: "bloodGroup",
      label: "Blood Group",
      minWidth: 100,
      format: (value) => value,
    },
    {
      id: "photo",
      label: "Photo",
      minWidth: 80,
      format: (_value, row) =>
        row.idProof ? (
          <Box
            component="img"
            src={row.photo_image_path}
            sx={{ width: 40, height: 40, objectFit: "cover", borderRadius: 1 }}
          />
        ) : (
          "-"
        ),
    },
    {
      id: "idProof",
      label: "ID Proof",
      minWidth: 80,
      format: (_value, row) =>
        row.idProof ? (
          <Box
            component="img"
            src={row.idProof_image_path}
            sx={{ width: 40, height: 40, objectFit: "cover", borderRadius: 1 }}
          />
        ) : (
          "-"
        ),
    },
    {
      id: "employmentType",
      label: "Employment Type",
      minWidth: 120,
      format: (value) => value,
    },
    {
      id: "salary",
      label: "Salary",
      minWidth: 100,
      format: (value) => value,
    },
    {
      id: "status",
      label: "Status",
      minWidth: 100,
      align: "center",
      format: (value) => <StatusChip active={value === "Active"} />,
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 120,
      align: "right",
      format: (_value, row) => (
        <ActionIcons
          actions={[
            {
              icon: <EditIcon />,
              tooltip: "Edit",
              onClick: () => navigate(`/admin/employees/${row.id}`),
            },
            {
              icon: <DeleteIcon />,
              isDelete: true,
              tooltip: "Delete",
              onClick: () =>
                setDeleteDialog({ open: true, id: row.id, title: row?.name }),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <PageWrapper loading={loading}>
      <PageHeader
        title="Employees"
        showSearch
        searchPlaceholder="Search employees..."
        onSearchChange={(value) => setSearch(value)}
        defaultSearchValue={search}
        primaryButton={{
          label: "Add Employee",
          icon: <AddIcon />,
          onClick: () => {
            navigate("/admin/employees/create");
          },
        }}
      />

      <DataTable
        columns={columns}
        rows={employees}
        totalCount={totalCount}
        onPaginationChange={handlePaginationChange}
        page={page}
        limit={limit}
      />
      <DeleteConfirmation
        open={deleteDialog.open}
        title={"Delete Employee - " + deleteDialog?.title}
        message="Are you sure you want to delete this employee? This action cannot be undone."
        apiEndpoint={"api/deleteEmployee/"}
        deleteId={deleteDialog.id}
        onCancel={() => setDeleteDialog({ open: false, id: 0, title: null })}
        refetchApi={() => {
          setPage(0);
          getEmployees();
        }}
      />
    </PageWrapper>
  );
}
